/**
 * HeroSection — Full-bleed hero with split layout, team image, floating badges
 * Design: Navy background, team image with "Instant Prequalification" badge
 */

import React from "react";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

// Team meeting image - diverse team around conference table with charts
const TEAM_IMG = "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/b30fucfn_hero_home_picture.webp";

const trustBadges = [
  "No Upfront Fees",
  "99% Approval Rate",
  "Funding in 24-48hrs",
];

export default function HeroSection() {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center bg-[#0f2d52] overflow-hidden pt-40">
      {/* Triangle Background Motif */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-5">
        <svg viewBox="0 0 800 600" className="absolute w-full h-full">
          <polygon points="400,50 750,550 50,550" stroke="#48d480" strokeWidth="2" fill="none" />
          <polygon points="400,100 700,530 100,530" stroke="#48d480" strokeWidth="1.5" fill="none" />
          <polygon points="400,150 650,510 150,510" stroke="#48d480" strokeWidth="1" fill="none" />
        </svg>
      </div>

      <div className="container relative z-10 py-16 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Content */}
          <div className="max-w-xl">
            {/* Tag */}
            <div className="inline-flex items-center gap-2 bg-[#48d480]/20 border border-[#48d480]/40 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#48d480] animate-pulse" />
              <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#48d480] text-xs font-semibold tracking-wide">
                Fast Funding • No Hidden Fees
              </span>
            </div>

            {/* Headline */}
            <h1
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.1] mb-6"
            >
              Get Your Business
              <br />
              <span className="text-[#48d480]">Funded Today</span>
            </h1>

            {/* Subhead */}
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/70 text-lg md:text-xl leading-relaxed mb-8 max-w-lg"
            >
              Access $5K to $5M+ in business funding. SBA loans, equipment financing, lines of credit, and more — with expert guidance every step of the way.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                to="/apply"
                data-testid="hero-apply-btn"
                className="flex items-center justify-center gap-2 text-base bg-[#e8821c] hover:bg-[#d67518] text-white font-bold px-8 py-4 rounded-lg transition-colors shadow-lg"
              >
                Apply Now <ArrowRight size={18} />
              </Link>
              <a
                href="#funding-finder"
                data-testid="hero-eligibility-btn"
                className="btn-outline-white flex items-center justify-center gap-2 text-base"
              >
                Check My Eligibility
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4">
              {trustBadges.map((badge) => (
                <div key={badge} className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-[#48d480]" />
                  <span
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-white/70 text-sm font-medium"
                  >
                    {badge}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Team Image with Floating Badges */}
          <div className="hidden lg:block relative">
            {/* Main Image Container with Navy Border */}
            <div className="relative">
              {/* Navy border frame */}
              <div className="absolute -inset-3 bg-[#0f2d52] rounded-2xl" />
              
              {/* Image */}
              <div className="relative rounded-xl overflow-hidden shadow-2xl border-4 border-[#0f2d52]">
                <img
                  src={TEAM_IMG}
                  alt="Professional team meeting"
                  className="w-full h-[480px] object-cover"
                />
              </div>

              {/* Floating "Instant Prequalification" Badge - Bottom Left */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#48d480]/20 flex items-center justify-center">
                  <CheckCircle size={22} className="text-[#48d480]" />
                </div>
                <div>
                  <div
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-[#0f2d52] font-bold text-sm"
                  >
                    Instant Prequalification
                  </div>
                  <div
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-gray-500 text-xs"
                  >
                    No credit impact
                  </div>
                </div>
              </div>

              {/* Triangle Logo - Top Right Corner */}
              <div className="absolute -top-3 -right-3 opacity-30">
                <svg width="80" height="80" viewBox="0 0 100 100">
                  <polygon points="50,10 90,85 10,85" stroke="#48d480" strokeWidth="3" fill="none" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
